/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import UnifiedProductSelectionGrid from "./offer/UnifiedProductSelectionGrid";
import OfferPageDetailsForm from "./offer/OfferPageDetailsForm";
import {
  createOfferPage,
  addProductsToOfferPage,
} from "@/services/offerPageService";
import { fetchProducts } from "@/services/productService";
import { fetchSubscriptions } from "@/data/subscriptionData";
import NoProductsWarning from "./offer/NoProductsWarning";
import { supabase } from "@/integrations/supabase/client";
import { useGlobalEvents } from "@/hooks/useGlobalEvents.ts";
import { Item } from "@/utils/dashboardDataUtils";

interface OfferPageFormProps {
  onClose: () => void;
}

const OfferPageForm: React.FC<OfferPageFormProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { dispatchOfferCreated } = useGlobalEvents();
  const [description, setDescription] = useState("");
  const [heading, setHeading] = useState("");
  const [title, setTitle] = useState("");
  const [items, setItems] = useState<Item[]>([]);
  const [selectedItems, setSelectedItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const {
          data: { user },
          error,
        } = await supabase.auth.getUser();
        if (error) {
          console.error("Error fetching user:", error.message);
          toast({
            title: "Error",
            description: "Could not get user information",
            variant: "destructive",
          });
          return;
        }

        if (user?.id) {
          setUserId(user.id);
        } else {
          console.warn("No user found");
          toast({
            title: "Authentication Required",
            description: "Please log in to create bundle pages",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Error in getCurrentUser:", error);
      }
    };

    getCurrentUser();
  }, [toast]);

  useEffect(() => {
    const fetchAllItems = async () => {
      if (!userId) {
        console.log("No user ID available, skipping fetch");
        return;
      }

      try {
        setLoading(true);

        // Fetch products using the service
        const products = await fetchProducts(userId);

        // Fetch subscriptions
        const subscriptions = await fetchSubscriptions();

        // Filter products for current user and active status
        const userProducts = products.filter((product) => {
          const isUserProduct = product.user_id === userId;
          const isActive = product.status === "active";
          return isUserProduct && isActive;
        });

        // Filter subscriptions for current user and active status
        const userSubscriptions = subscriptions.filter((subscription) => {
          const isUserSubscription = subscription.user_id === userId;
          const isActive = subscription.status === "active";
          return isUserSubscription && isActive;
        });

        const allItems = [...userProducts, ...userSubscriptions];

        setItems(allItems);
        
        const lastCreatedProductId = sessionStorage.getItem('lastCreatedProductId');
        if (lastCreatedProductId) {
          const productToSelect = allItems.find(item => item.id === lastCreatedProductId);
          if (productToSelect) {
            setSelectedItems([productToSelect]);
          }
          sessionStorage.removeItem('lastCreatedProductId'); 
        }

        if (allItems.length === 0) {
          console.warn("No active items found for user:", userId);
          toast({
            title: "You didn't create any products yet",
            description:
              "You need to create some active products or subscriptions before creating an offer page.",
            variant: "default",
          });
        }
      } catch (error) {
        console.error("Error fetching items:", error);
        toast({
          title: "Error",
          description: "Failed to fetch products and subscriptions",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchAllItems();
    }
  }, [userId, toast]);

  const handleItemSelect = (item: Item) => {
    setSelectedItems((prev) => {
      const isAlreadySelected = prev.some((p) => p.id === item.id);
      const hasDifferentBillingPeriod = prev.some(
        (p) =>
          p.billing_period !== item.billing_period &&
          p.billing_period !== "one_time" &&
          item.billing_period !== "one_time"
      );
      if (hasDifferentBillingPeriod) {
        toast({
          title: "Invalid Selection",
          description:
            "You can only select one subscription type along with one-time products.",
          variant: "destructive",
        });
        return prev;
      }
      return isAlreadySelected
        ? prev.filter((p) => p.id !== item.id)
        : [...prev, item];
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!userId) {
      toast({
        title: "Authentication Required",
        description: "Please log in to create bundles pages",
        variant: "destructive",
      });
      return;
    }

    if (selectedItems.length === 0) {
      toast({
        title: "No Items Selected",
        description: "Please select at least one product or subscription",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const offerPage = await createOfferPage({
        heading: heading,
        subheading: description,
        slug: heading.toLowerCase().replace(/\s+/g, "-"),
        call_to_action: "Buy Now",
      });

      if (!offerPage || !offerPage.id) {
        throw new Error("Failed to create bundle");
      }

      await addProductsToOfferPage(
        offerPage.id,
        selectedItems.map((item) => item.id)
      );

      toast({
        description: "New Bundle created successfully",
        variant: "success",
        className: "border-none",
      });

      dispatchOfferCreated(offerPage);
      onClose();

      if (location.pathname === "/dashboard"){
        navigate('/dashboard');
      }

    } catch (error) {
      console.error("Error creating bundle:", error);
      toast({
        title: "Error",
        description: "Failed to create bundle",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <p className="text-muted-foreground">
            Loading your products and subscriptions...
          </p>
        </div>
      </div>
    );
  }

  if (!loading && items.length === 0) {
    return <NoProductsWarning offerId="no-offer" productCount={0} />;
  }

  return (
    <div className="flex flex-col h-full max-h-[100vh] offer-page-form-container">
      <form onSubmit={handleSubmit} className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto">
          <div className="sticky top-0 z-10 bg-white pt-6 pb-4 px-4 sm:px-6">
            <div className="flex items-start justify-between mb-2">
              <h2 className="text-3xl font-semibold">Create Checkout Page</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Create a custom page that showcases multiple products and
              subscriptions
            </p>
          </div>
          <OfferPageDetailsForm
            description={description}
            setDescription={setDescription}
            heading={heading}
            setHeading={setHeading}
            title={title}
            setTitle={setTitle}
            onClose={onClose}
          />
          <div className="pt-6 px-4 sm:px-6 pb-24">
            <UnifiedProductSelectionGrid
              availableItems={items}
              selectedItems={selectedItems}
              onItemSelect={handleItemSelect}
              loading={loading}
            />
          </div>
        </div>
        <div className="sticky bottom-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-t pt-4 pb-4 px-4">
          <div className="flex flex-col items-center gap-3 max-w-7xl mx-auto">
            <div>
              <p className="text-sm text-muted-foreground">
                {selectedItems.length} item{selectedItems.length !== 1 ? "s" : ""}{" "}
                selected
              </p>
            </div>
            <Button
              type="submit"
              disabled={isSubmitting || selectedItems.length === 0}
              className="gradient-bg w-full rounded-md"
            >
              {isSubmitting ? "Creating Checkout Page..." : "Create Checkout Page"}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default OfferPageForm;